import { useEffect, useRef, useState } from 'react'
import { featureRequestApi } from '../services/api'

const OPEN_EVENT = 'open-request-feature-modal'

const TITLE_MAX = 140
const DESC_MAX = 1000

export function openRequestFeatureModal() {
  window.dispatchEvent(new Event(OPEN_EVENT))
}

type Status = 'idle' | 'submitting' | 'success' | 'error'

/**
 * Self-contained feature-request modal.
 * Mirrors BetaModal: window event opener, ESC + overlay close, focus trap on
 * first field, body scroll lock while open. POSTs to /v1/feature-requests
 * and replaces the form with a success card on 201. Email is optional and
 * never displayed publicly — see the design-review decisions doc.
 */
export default function RequestFeatureModal() {
  const [isOpen, setIsOpen] = useState(false)
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [email, setEmail] = useState('')
  const [status, setStatus] = useState<Status>('idle')
  const [errorMsg, setErrorMsg] = useState('')
  const firstFieldRef = useRef<HTMLInputElement>(null)
  const triggerRef = useRef<Element | null>(null)

  useEffect(() => {
    const open = () => {
      // Capture trigger so we can restore focus on close (a11y).
      triggerRef.current = document.activeElement
      setStatus('idle')
      setErrorMsg('')
      setTitle('')
      setDescription('')
      setEmail('')
      setIsOpen(true)
    }
    window.addEventListener(OPEN_EVENT, open)
    return () => window.removeEventListener(OPEN_EVENT, open)
  }, [])

  useEffect(() => {
    if (!isOpen) return
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const t = setTimeout(() => firstFieldRef.current?.focus(), 50)
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false)
    }
    window.addEventListener('keydown', onKey)
    return () => {
      document.body.style.overflow = prevOverflow
      window.removeEventListener('keydown', onKey)
      clearTimeout(t)
      // Restore focus to the element that opened the modal.
      const trigger = triggerRef.current
      if (trigger && trigger instanceof HTMLElement) trigger.focus()
    }
  }, [isOpen])

  const close = () => setIsOpen(false)

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    const t = title.trim()
    const d = description.trim()
    const em = email.trim()
    if (!t || !d) return
    setStatus('submitting')
    setErrorMsg('')
    try {
      await featureRequestApi.submit({
        title: t,
        description: d,
        email: em || undefined,
      })
      setStatus('success')
    } catch (err) {
      setStatus('error')
      setErrorMsg(err instanceof Error ? err.message : 'Something went wrong.')
    }
  }

  if (!isOpen) return null

  return (
    <div
      className="v2-modal-overlay"
      onClick={(e) => { if (e.target === e.currentTarget) close() }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="request-feature-modal-title"
    >
      <div className="v2-modal-card v2-modal-card-tall">
        <button
          type="button"
          className="v2-modal-close"
          onClick={close}
          aria-label="Close"
        >
          ✕
        </button>

        <h2 id="request-feature-modal-title" className="v2-modal-title">
          Tell us what to ship next
        </h2>
        <p className="v2-modal-lede">
          Describe a deployment workflow your agent still has to do by hand.
          We read every one.
        </p>

        {status === 'success' ? (
          <div className="v2-modal-success">
            ✓ Thanks — we've got your idea. We'll keep you posted as it makes the roadmap.
          </div>
        ) : (
          <form className="v2-modal-form" onSubmit={submit}>
            <div className="v2-modal-field">
              <label className="v2-modal-label" htmlFor="fr-title">
                Title
                <span className="v2-modal-counter">{title.length}/{TITLE_MAX}</span>
              </label>
              <input
                id="fr-title"
                ref={firstFieldRef}
                className="v2-modal-input"
                type="text"
                required
                maxLength={TITLE_MAX}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                disabled={status === 'submitting'}
                placeholder="One-line summary"
              />
            </div>
            <div className="v2-modal-field">
              <label className="v2-modal-label" htmlFor="fr-desc">
                Description
                <span className="v2-modal-counter">{description.length}/{DESC_MAX}</span>
              </label>
              <textarea
                id="fr-desc"
                className="v2-modal-input v2-modal-textarea"
                required
                maxLength={DESC_MAX}
                rows={4}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                disabled={status === 'submitting'}
                placeholder="What workflow are you doing by hand today?"
              />
            </div>
            <div className="v2-modal-field">
              <label className="v2-modal-label" htmlFor="fr-email">
                Email <span className="v2-modal-label-hint">(optional)</span>
              </label>
              <input
                id="fr-email"
                className="v2-modal-input"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={status === 'submitting'}
                placeholder="you@company.com"
              />
            </div>
            <button
              type="submit"
              className="v2-modal-submit"
              disabled={status === 'submitting' || !title.trim() || !description.trim()}
            >
              {status === 'submitting' ? 'Sending…' : 'Send request'}
            </button>
            {status === 'error' && errorMsg && (
              <div className="v2-modal-error">{errorMsg}</div>
            )}
          </form>
        )}
      </div>
    </div>
  )
}
